import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { colorWheelColors } from "../../constants/constants";

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  position?: "top" | "bottom";
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
  buttonClassName = "",
  dropdownClassName = "",
  position = "top",
}) => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const customColorInputRef = useRef<HTMLInputElement>(null);

  const calculatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const dropdownWidth = 230;
      const dropdownHeight = 200;

      let top = 0;
      let left = rect.left + scrollX;

      if (position === "top") {
        top = rect.top + scrollY - dropdownHeight - 8;
      } else {
        top = rect.bottom + scrollY + 8;
      }

      setDropdownStyles({
        position: "absolute",
        top: `${top}px`,
        left: `${left}px`,
        width: `${dropdownWidth}px`,
        height: `${dropdownHeight}px`,
      });
    }
  };

  useEffect(() => {
    if (isColorPickerOpen) {
      calculatePosition();
      window.addEventListener("scroll", calculatePosition, true);
      window.addEventListener("resize", calculatePosition);
    }

    return () => {
      window.removeEventListener("scroll", calculatePosition, true);
      window.removeEventListener("resize", calculatePosition);
    };
  }, [isColorPickerOpen, position]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".color-picker-portal-content")
      ) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isColorPickerOpen]);

  const toggleColorPicker = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsColorPickerOpen((prev) => !prev);
    }
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsColorPickerOpen(false);
  };

  const handleCustomColorClick = () => {
    customColorInputRef.current?.click();
    setIsColorPickerOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const renderDropdown = () => {
    if (!isColorPickerOpen) return null;

    return createPortal(
      <div
        className={`p-2 flex items-center flex-wrap gap-0 gap-x-1.5 border-gray-600 border 
        bg-secondary-light dark:bg-secondary-dark z-[999999] rounded-lg shadow-xl color-picker-portal-content ${dropdownClassName}`}
        style={dropdownStyles}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Predefined colors from color wheel */}
        {Object.values(colorWheelColors).map((color: string, index: number) => {
          const isSelected = value === color;
          return (
            <div
              key={index}
              className={`
                cursor-pointer rounded-md border-2 transition-all hover:scale-110
                ${isSelected ? "dark:border-white" : "dark:border-gray-700"} 
                ${isSelected ? "border-black" : "border-[#d6d6d6]"}  
              `}
              style={{
                width: "17.5%",
                height: "20.5%",
                backgroundColor: color,
              }}
              onClick={() => handleColorSelect(color)}
              title={color}
            />
          );
        })}

        <div
          className="text-sm text-[#575b63] dark:text-gray-400 h-fit ml-2 cursor-pointer hover:text-[#3d4046] dark:hover:text-gray-300 transition-colors"
          onClick={handleCustomColorClick}
        >
          + Custom
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={`relative ${className}`} ref={colorPickerRef}>
      <input
        ref={customColorInputRef}
        type="color"
        onChange={handleCustomColorChange}
        tabIndex={-1}
        value={value}
        className="opacity-0 w-0 h-0 pointer-events-none invisible absolute"
        disabled={disabled}
      />

      <button
        ref={buttonRef}
        type="button"
        className={`border-white border mt-2 rounded-lg w-20 h-8 transition-opacity ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-90"
        } ${buttonClassName}`}
        onClick={toggleColorPicker}
        disabled={disabled}
        style={{
          backgroundColor: value || "#182439",
        }}
      />

      {renderDropdown()}
    </div>
  );
};

export default ColorPicker;
